import React from "react";
import { Routes, Route, Navigate, useParams } from "react-router-dom";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";
import PublicRoute from "./components/PublicRoute";
import MainLayout from "./components/layout/MainLayout";
import BotLayout from "./components/layout/BotLayout";
import { AuthProvider } from "./contexts/AuthContext";
import { TenantProvider } from "./contexts/TenantContext";
import { Toaster } from "./components/ui/toaster";
import UserHome from "./pages/UserHome";
import Billing from "./pages/Billing";
import OrganizationSettings from "./pages/OrganizationSettings";
import AdminUsers from "./pages/AdminUsers";
import BotsList from "./pages/BotsList";
import CreateBotPage from "./pages/CreateBotPage";
import StartPage from "./pages/StartPage";
import BotDetail from "./pages/BotDetail";
import QueryInterface from "./pages/QueryInterface";
import ManageSourcesPage from "./pages/ManageSourcesPage";
import SettingsTab from "./pages/SettingsTab";
import Login from "./pages/Login";
import Register from "./pages/Register";
import AcceptInvite from "./pages/AcceptInvite";
import NotFound from "./pages/NotFound";
import { RouteErrorPage } from "./pages/ErrorPages";
import WidgetTab from "./pages/WidgetTab";
import Intelligence from "./pages/Intelligence";
import ConversationsPage from "./pages/ConversationsPage";
import ContactsPage from "./pages/ContactsPage";
import IntegrationsPage from "./pages/IntegrationsPage";
import IntegrationDetailPage from "./pages/IntegrationDetailPage";
import ConnectAuthorize from "./pages/ConnectAuthorize";
import "./i18n/config";

// La vecchia sezione WhatsApp e' diventata l'app WhatsApp: i link salvati
// portano li'.
function VecchiaSezioneWhatsApp() {
  const { id } = useParams();
  return <Navigate to={`/bots/${id}/integrazioni/whatsapp`} replace />;
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <TenantProvider>
          <div className="min-h-screen bg-background">
            <Routes>
              {/* Landing pubblica (onboarding v2): chiunque raggiunga PingUp
                  atterra qui — il prompt si scrive senza account, la
                  registrazione arriva al momento del "Crea" (stile Lovable).
                  Il path esatto "/" vince sul catch-all protetto "/*". */}
              <Route path="/" element={<StartPage />} />

              {/* Public routes */}
              <Route path="/health" element={<HealthCheck />} />

              {/* Invite acceptance - accessible to both authenticated and non-authenticated users */}
              <Route path="/invites/:token/accept" element={<AcceptInvite />} />

              {/* WordPress plugin connect flow - consent screen. Auth is gated
                  in-page (not via ProtectedRoute) so the OAuth query string
                  survives the login round-trip via ?returnUrl= */}
              <Route path="/connect/authorize" element={<ConnectAuthorize />} />

              {/* Authentication routes - only accessible when not authenticated */}
              <Route
                path="/login"
                element={
                  <PublicRoute>
                    <Login />
                  </PublicRoute>
                }
              />
              <Route
                path="/register"
                element={
                  <PublicRoute>
                    <Register />
                  </PublicRoute>
                }
              />

              {/* La vecchia scelta del piano dopo la registrazione non c'e' piu':
                  si parte gratis e il piano si sceglie nell'onboarding, quando le fonti
                  superano il gratuito, o dalla pagina Abbonamento. I link vecchi portano li'. */}
              <Route path="/select-plan" element={<Navigate to="/billing" replace />} />

              {/* Onboarding (v2) fuori dalla shell: al PRIMO bot il wizard è
                  fullscreen senza sidebar né rotte (focus totale); con bot
                  già esistenti la pagina si auto-avvolge nel MainLayout. */}
              <Route
                path="/bots/new"
                element={
                  <ProtectedRoute>
                    <CreateBotPage />
                  </ProtectedRoute>
                }
              />

              {/* Protected routes with layout */}
              <Route
                path="/*"
                element={
                  <ProtectedRoute>
                    <MainLayout>
                      <Routes>
                        {/* La home della dashboard vive su /home: la radice
                            "/" è la landing pubblica del prompt. */}
                        <Route path="/home" element={<UserHome />} />
                        <Route path="/bots" element={<BotsList />} />
                        <Route path="/billing" element={<Billing />} />
                        <Route
                          path="/organization"
                          element={<OrganizationSettings />}
                        />
                        {/* Admin routes - protected for system admins only */}
                        <Route
                          path="/admin"
                          element={
                            <ProtectedAdminRoute>
                              <AdminUsers />
                            </ProtectedAdminRoute>
                          }
                        />
                        <Route path="/bots/:id" element={<BotDetail />} />
                        <Route
                          path="/bots/:id/sources"
                          element={
                            <BotLayout title="Knowledge Sources">
                              <ManageSourcesPage />
                            </BotLayout>
                          }
                        />
                        <Route
                          path="/bots/:id/playground"
                          element={
                            <BotLayout title="Query Playground">
                              <QueryInterface />
                            </BotLayout>
                          }
                        />
                        <Route
                          path="/bots/:id/widget"
                          element={
                            <BotLayout title="Widget Configuration">
                              <WidgetTab />
                            </BotLayout>
                          }
                        />
                        <Route
                          path="/bots/:id/whatsapp"
                          element={<VecchiaSezioneWhatsApp />}
                        />
                        <Route
                          path="/bots/:id/conversations"
                          element={
                            <BotLayout title="Conversations">
                              <ConversationsPage />
                            </BotLayout>
                          }
                        />
                        <Route
                          path="/bots/:id/contatti"
                          element={
                            <BotLayout title="Contact form">
                              <ContactsPage />
                            </BotLayout>
                          }
                        />
                        <Route
                          path="/bots/:id/integrazioni"
                          element={
                            <BotLayout title="Apps & Integrations">
                              <IntegrationsPage />
                            </BotLayout>
                          }
                        />
                        <Route
                          path="/bots/:id/integrazioni/:app"
                          element={
                            <BotLayout title="Apps & Integrations">
                              <IntegrationDetailPage />
                            </BotLayout>
                          }
                        />
                        <Route
                          path="/bots/:id/settings"
                          element={
                            <BotLayout title="Bot Settings">
                              <SettingsTab />
                            </BotLayout>
                          }
                        />
                        <Route
                          path="/bots/:id/intelligence"
                          element={
                            <BotLayout title="Analytics">
                              <Intelligence />
                            </BotLayout>
                          }
                        />
                        {/* Legacy route for backwards compatibility */}
                        <Route
                          path="/bots/:id/query"
                          element={<QueryInterface />}
                        />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </MainLayout>
                  </ProtectedRoute>
                }
              />
            </Routes>
          </div>
          <Toaster />
        </TenantProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

// Simple health check component for monitoring
const HealthCheck = () => {
  return (
    <div className="p-4">
      <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded">
        <strong>Status:</strong> OK
      </div>
    </div>
  );
};

export default App;
